import { array, fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq, or } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button';
import ContextMenu from 'umg-ember-backend/components/ui/context-menu';
import { imageUrl } from 'umg-ember-backend/helpers/image-url';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class UiAssetPrevieComponent extends Component {
  // Services
  @service abilities;
  @service recordStatus;

  // Defaults
  @tracked previewHasError = false;

  defaultMetaInformation = {
    audio: [['mime', 'fileSize']],
    document: [['mime', 'fileSize']],
    image: [['resolution'], ['mime', 'fileSize']],
    video: [['durationString'], ['mime', 'fileSize']],
  };

  // Getter and setter
  get cardLayout() {
    return this.args.cardLayout ?? 'relation';
  }

  get hasPreviewImage() {
    const hasMedia = this.args.asset.isImage || this.args.asset.isVideo;

    return hasMedia && !this.previewHasError;
  }

  get metaInformation() {
    const assetType = this.args.asset.assetType;
    const assetTypeInformation = this.defaultMetaInformation[assetType] ?? [];

    if (!this.args.columns) {
      return assetTypeInformation;
    }

    return [
      ...assetTypeInformation,
      ...this.args.columns.map((column) => [column.property]),
    ];
  }

  get isSelected() {
    let selectedObjects = this.args.selectedRelationalObjects;

    if (!selectedObjects) {
      return false;
    }

    return selectedObjects.slice(-1)[0] === this.args.asset;
  }

  get isSelectedToAssign() {
    let selectedObjects = this.args.selectedRelationalObjects;

    if (!selectedObjects) {
      return false;
    }

    return selectedObjects.includes(this.args.asset);
  }

  get objectFit() {
    const scale = ['1', '8', '120'].includes(this.args.asset.assetFolderId);

    return scale ? 'scale-down' : 'cover';
  }

  get isBlocked() {
    return this.recordStatus.isBlocked(this.args.asset);
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.asset);
  }

  // Functions
  selectAsset = () => {
    if (this.args.selectRelationalObjects) {
      this.args.selectRelationalObjects(this.args.asset);
    }
  };

  openAsset = () => {
    if (!this.args?.recordActions && !this.args.recordActions?.edit) {
      return;
    }

    let allowToRead = this.abilities.can('read', 'asset');
    if (allowToRead) {
      this.args.recordActions.edit(this.args.asset);
    }
  };

  showErrorImage = () => {
    this.previewHasError = true;
  };

  // Template
  <template>
    <div
      class={{bem
        (hash
          is-broken=@asset.hasError
          is-selected=this.isSelected
          is-selected-to-assign=this.isSelectedToAssign
          layout=this.cardLayout
          mime=(dasherize (or @asset.sourceFormat.mime ''))
          object-fit=this.objectFit
        )
      }}
      role='button'
      {{on 'click' this.selectAsset}}
      {{on 'dblclick' this.openAsset}}
      ...attributes
    >
      <div class={{bem 'wrapper'}}>
        {{#if this.isSelectedToAssign}}
          <div class={{bem 'selected-indicator'}}>
            {{svgJar 'selected' class='u-icon'}}
          </div>
        {{/if}}

        <div class={{bem 'media'}}>
          {{#if this.hasPreviewImage}}
            <img
              src={{imageUrl (array @asset '4/480')}}
              alt={{@asset.headline}}
              class={{bem 'thumbnail'}}
              {{on 'error' this.showErrorImage}}
            />
          {{else}}
            <div class={{bem 'mime'}}>
              {{#if @asset.sourceFormat.mime}}
                {{@asset.sourceFormat.mime}}
              {{else}}
                {{t 'ui.asset_preview.unknown_type'}}
              {{/if}}
            </div>
            {{#if this.previewHasError}}
              {{t 'ui.asset_preview.preview_not_available'}}
            {{/if}}
          {{/if}}

          {{#if @asset.hasError}}
            <div class={{bem 'overlay'}}>
              {{t 'ui.asset_preview.broken_asset'}}
            </div>
          {{/if}}
        </div>

        <div class={{bem 'information'}}>
          <div class={{bem 'headline'}}>
            {{#if this.isBlocked}}
              {{svgJar 'lock' class=(bem 'blocked-indicator')}}
              <EmberTooltip @text={{t 'ui.asset_preview.blocked'}} />
            {{/if}}
            {{@asset.displayLabel}}
          </div>
          {{#each this.metaInformation as |row|}}
            <div class={{bem 'row'}}>
              {{#each row as |value|}}
                {{#if (get @asset.sourceFormat value)}}
                  {{get @asset.sourceFormat value}}
                {{/if}}
              {{/each}}
            </div>
          {{/each}}
        </div>
        {{#if (eq this.cardLayout 'list')}}
          <div class={{bem 'context-menu'}}>
            <ContextMenu>
              <:trigger>
                <Button @style='grey' @icon='more' />
              </:trigger>

              <:content as |content|>
                <content.Item
                  @icon='edit'
                  @onClick={{fn @recordActions.edit @asset}}
                  @label={{t 'ui.asset_preview.edit'}}
                />

                <content.Item
                  @icon='delete'
                  @iconClass='u-color-error'
                  @onClick={{fn @recordActions.delete @asset}}
                  @label={{t 'ui.asset_preview.delete'}}
                  @isDisabled={{this.isDisabled}}
                />
              </:content>
            </ContextMenu>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
